import { Feed, Segment, Button } from 'semantic-ui-react';
import moment from 'moment';
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { TRANSLATION_FILES } from '../../../constants/translation';
import { displayMoment } from '../../../utils/displayTime';
import { SINGLE_STATUS_MAP } from '../../../constants/opportunityStatuses';
import { BANK_STATUS_MAP } from '../../../constants/bankStatesAndOppStage';

class TimelineLog extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    activityLog: PropTypes.arrayOf(PropTypes.object).isRequired,
    isFullWidth: PropTypes.bool,
    hasMore: PropTypes.bool,
    onLoadMore: PropTypes.func,
    isLoadingMore: PropTypes.bool,
    countryCode: PropTypes.string,
  };

  getBookingDisplayStatus = (statusKey) => {
    if (!statusKey) return '';
    const cleanKey = String(statusKey).toLowerCase();
    const statusObj = SINGLE_STATUS_MAP[cleanKey];
    const readableText = statusObj ? statusObj.text : statusKey;
    return this.props.t(readableText, { ns: TRANSLATION_FILES.constants });
  };

  getLoanDisplayStatus = (statusKey) => {
    if (!statusKey) return '';
    const cleanKey = String(statusKey).toLowerCase();
    const statusObj = BANK_STATUS_MAP[cleanKey];
    const readableText = statusObj ? statusObj.text : statusKey;
    return this.props.t(readableText, { ns: TRANSLATION_FILES.constants });
  };

  getCapitalizedStatus = (status) => {
    if (!status) return '';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  getEventTitle = (log) => {
    const { source_table, source_type } = log;
    switch (source_table) {
      case 'unit_resv_tokens':
      case 'ur_token_payments':
        return 'Payment details updated';
      case 'opportunities':
        // The booking timeline tracks status only — stage is not surfaced. The
        // create event marks the booking's creation; surface it as "Booking
        // created" (mirror leads-v2-web), while later changes read as "Booking
        // status updated".
        return source_type === 'create'
          ? 'Booking created'
          : 'Booking status updated';
      case 'bank_cases':
        return source_type === 'create'
          ? 'Loan created'
          : 'Loan status updated';
      case 'bank_case_activities':
        return 'Loan status updated';
      case 'lead_activities':
      case 'leads':
        return 'Lead status updated';
      case 'shared_notes':
        return 'Note is added';
      default:
        return 'System updated details';
    }
  };

  getLogMessageContent = (log) => {
    switch (log.source_table) {
      case 'unit_resv_tokens':
        return this.renderTokenAmountPaidLog(log);
      case 'ur_token_payments':
        return this.renderTokenPaidLog(log);
      case 'opportunities':
        return this.renderOpportunityStatus(log);
      case 'bank_cases':
        return this.renderLoanStatus(log);
      case 'shared_notes':
        return this.renderSharedNote(log);
      default:
        // Banker activity tables (lead_activities, bank_case_activities, leads)
        // and any other source without a custom renderer fall back to the
        // human-readable source_message, so the feed mirrors leads-v2-web
        // instead of silently dropping them.
        return this.renderGenericMessage(log);
    }
  };

  renderTokenAmountPaidLog(log) {
    const { t } = this.props;
    const before = log.data_before || {};
    const after = log.data_after || {};
    const elements = [];

    if (after.amount_minor_paid !== undefined && before.amount_minor_paid !== after.amount_minor_paid) {
      const currency = after.currency_code || before.currency_code || 'VND';

      const hasValidBefore = before.amount_minor_paid !== undefined && before.amount_minor_paid !== null;
      const displayString = hasValidBefore
        ? `${currency} ${before.amount_minor_paid} → ${currency} ${after.amount_minor_paid}`
        : `${currency} ${after.amount_minor_paid}`;

      elements.push(
        <div key="payment-amount" className="timeline-diff-line">
          <strong>{t('Total Payment')}:</strong> {displayString}
        </div>,
      );
    }

    return elements.length > 0 ? <div>{elements}</div> : null;
  }

  renderTokenPaidLog(log) {
    const { t } = this.props;
    const before = log.data_before || {};
    const after = log.data_after || {};
    const elements = [];

    if (after.status !== undefined && before.status !== after.status) {
      elements.push(
        <div key="payment-status" className="timeline-diff-line">
          <strong>{t('Payment status')}:</strong> {before.status ? `${t(this.getCapitalizedStatus(before.status), { ns: TRANSLATION_FILES.constants })} → ` : ''}{t(this.getCapitalizedStatus(after.status), { ns: TRANSLATION_FILES.constants })}
        </div>,
      );
    }

    if (after.amount_minor !== undefined && before.amount_minor !== after.amount_minor) {
      const currency = after.currency_code || before.currency_code || 'VND';

      const hasValidBefore = before.amount_minor !== undefined && before.amount_minor !== null;
      const displayString = hasValidBefore
        ? `${currency} ${before.amount_minor} → ${currency} ${after.amount_minor}`
        : `${currency} ${after.amount_minor}`;

      elements.push(
        <div key="payment-amount" className="timeline-diff-line">
          <strong>{t('Payment Made')}:</strong> {displayString}
        </div>,
      );
    }

    return elements.length > 0 ? <div>{elements}</div> : null;
  }

  renderOpportunityStatus(log) {
    const { t } = this.props;
    const before = log.data_before || {};
    const after = log.data_after || {};
    const elements = [];

    // Status changes only — stage is intentionally omitted from the timeline.
    // An opportunity log with no status change renders nothing and is skipped.
    if (after.sr_status !== undefined && before.sr_status !== after.sr_status) {
      elements.push(
        <div key="booking-status" className="timeline-diff-line">
          <strong>{t('Booking Status')}:</strong> {before.sr_status ? `${this.getBookingDisplayStatus(before.sr_status)} → ` : ''}{this.getBookingDisplayStatus(after.sr_status)}
        </div>,
      );
    }

    return elements.length > 0 ? <div>{elements}</div> : null;
  }

  renderLoanStatus(log) {
    const before = log.data_before || {};
    const after = log.data_after || {};
    const meta = log.source_metadata || {};

    // Pre-formatted bank details string resolved by mhub-log-api
    // (bank · branch · package · amount). Empty when not a bank-case log.
    const loanSubtitle = meta.bank_details || '';

    if (log.source_type === 'create') {
      if (!loanSubtitle) return null;
      return (
        <div style={{ color: '#adb5bd', fontSize: '12px', marginTop: '2px' }}>
          {loanSubtitle}
        </div>
      );
    }

    if (after.status !== undefined && before.status !== after.status) {
      const beforeStatusText = this.getLoanDisplayStatus(before.status) || before.status;
      const afterStatusText = this.getLoanDisplayStatus(after.status) || after.status;

      return (
        <div className="timeline-diff-line">
          <div>
            {before.status ? `${beforeStatusText} → ` : ''}
            <strong>{afterStatusText}</strong>
          </div>

          {loanSubtitle && (
            <div style={{ color: '#adb5bd', fontSize: '12px', marginTop: '2px' }}>
              {loanSubtitle}
            </div>
          )}
        </div>
      );
    }

    return null;
  }

  renderSharedNote(log) {
    const after = log.data_after || {};
    if (after.note) {
      const decodedNote = decodeURIComponent(after.note);
      return <div className="timeline-diff-line">“{decodedNote}”</div>;
    }
    return null;
  }

  // Fallback for sources without a custom renderer: show the API's
  // human-readable source_message. Null (skipped) when there's no message.
  renderGenericMessage(log) {
    if (!log.source_message) return null;
    return <div className="timeline-diff-line">{log.source_message}</div>;
  }

  getUserName(log) {
    const actor = log.actor;
    const actorName = actor && (typeof actor === 'string' ? actor : actor.name);
    const metadata = log.source_metadata || {};

    return actorName || log.actor_name || metadata.mu_name || 'System';
  }

  render() {
    const {
      t, activityLog = [], isFullWidth, hasMore, onLoadMore, isLoadingMore, countryCode,
    } = this.props;
    const containerClasses = `showroom-timeline-container ${isFullWidth ? 'full-width-timeline' : ''}`;

    if (activityLog.length === 0) {
      return (
        <Segment basic className={containerClasses}>
          <div className="no-activity-placeholder">
            {t('No activity yet')}
          </div>
        </Segment>
      );
    }

    let lastRenderedDate = null;
    const sortedLog = [...activityLog].sort((a, b) =>
      moment(b.source_updated_at).valueOf() - moment(a.source_updated_at).valueOf(),
    );

    return (
      <Segment basic className={containerClasses}>
        <Feed className="custom-timeline-feed">
          {sortedLog.map((log) => {
            const userName = this.getUserName(log);
            const timestamp = displayMoment(log.source_updated_at, countryCode).format('h:mm a');
            const logContent = this.getLogMessageContent(log);

            if (!logContent) return null;

            // Same zone as `timestamp` above: the day header groups the events shown beneath it,
            // so reading the two in different zones would file an event under the wrong heading.
            const currentDateLabel = displayMoment(log.source_updated_at, countryCode).format('ddd, MMM D, YYYY').toUpperCase();
            const showDateHeader = currentDateLabel !== lastRenderedDate;

            if (showDateHeader) {
              lastRenderedDate = currentDateLabel;
            }

            return (
              <React.Fragment key={log.id}>
                {showDateHeader && (
                  <div className="timeline-day-header">
                    {currentDateLabel}
                  </div>
                )}

                <Feed.Event className="timeline-event-row">
                  <div className="timeline-node-container">
                    <span className="timeline-circle-node" />
                  </div>

                  <Feed.Content className="timeline-body-content">
                    <Feed.Summary className="timeline-header-title">
                      {t(this.getEventTitle(log))}
                    </Feed.Summary>

                    <Feed.Extra text className="timeline-changes-wrapper">
                      {logContent}
                    </Feed.Extra>

                    <Feed.Meta className="timeline-footer-meta">
                      {timestamp} &bull; {userName}
                    </Feed.Meta>
                  </Feed.Content>
                </Feed.Event>
              </React.Fragment>
            );
          })}
        </Feed>

        {hasMore && (
          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <Button
              basic
              compact
              size="small"
              loading={isLoadingMore}
              disabled={isLoadingMore}
              onClick={onLoadMore}
            >
              {t('Load more activity')}
            </Button>
          </div>
        )}
      </Segment>
    );
  }
}

export default withTranslation(TRANSLATION_FILES.translation)(connect(state => ({
  countryCode: state.auth && state.auth.company && state.auth.company.countryCode,
}))(TimelineLog));



// WEBPACK FOOTER //
// ./src/containers/Opportunities/Overview/TimelineLog.jsx